import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Heart } from 'lucide-react';

export type SentimentLabel = 'positive' | 'neutral' | 'negative' | 'na';

export type Sentiment = {
  label: SentimentLabel;
  intensity: number | null;
};

const SENTIMENT_COPY: Record<
  SentimentLabel,
  { label: string; className: string; badgeClass: string; iconClass: string }
> = {
  positive: {
    label: 'Positivo',
    className: 'text-green-700 dark:text-green-300',
    badgeClass:
      'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/30',
    iconClass: 'text-green-500',
  },
  neutral: {
    label: 'Neutrale',
    className: 'text-slate-600 dark:text-slate-300',
    badgeClass:
      'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900/40',
    iconClass: 'text-slate-400',
  },
  negative: {
    label: 'Negativo',
    className: 'text-red-700 dark:text-red-300',
    badgeClass: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30',
    iconClass: 'text-red-500',
  },
  na: {
    label: 'N/A',
    className: 'text-muted-foreground',
    badgeClass: 'border-border bg-muted text-muted-foreground',
    iconClass: 'text-muted-foreground',
  },
};

const SENTIMENT_ICON_CLASS = 'h-3.5 w-3.5';

export function SentimentPill({ sentiment }: { sentiment?: Sentiment | null }) {
  if (!sentiment) return null;

  const safeLabel: SentimentLabel =
    sentiment?.label && SENTIMENT_COPY[sentiment.label] ? sentiment.label : 'na';
  const { label, className, badgeClass, iconClass } = SENTIMENT_COPY[safeLabel];
  const scoreText = sentiment.intensity ? ` · ${sentiment.intensity}/10` : '';

  return (
    <Badge
      variant="outline"
      className={cn('gap-1 font-medium px-2 py-1 text-xs rounded-full', badgeClass, className)}
    >
      <Heart className={cn(SENTIMENT_ICON_CLASS, iconClass)} />
      {label}
      {scoreText}
    </Badge>
  );
}
